import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, AlertCircle, ClipboardCheck, CheckCircle2, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;
const LOCALES = { es: "es-ES", en: "en-US", pt: "pt-BR" };

export default function RequestStatus() {
  const { t, language } = useLanguage();
  const rs = t.requestStatus;
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const formatDate = (iso) => new Date(iso).toLocaleString(LOCALES[language] || LOCALES.es);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim() || loading) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await axios.get(`${API}/api/distribution-requests/status/${encodeURIComponent(code.trim())}`);
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) setError(rs.notFoundError);
      else if (err.response?.status === 429) setError(rs.rateLimitError);
      else setError(rs.genericError);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError("");
    setCode("");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="request-status-page">
      <div className="max-w-2xl mx-auto px-6 py-16 sm:py-24">
        <Link to="/" data-testid="request-status-back-home" className="inline-flex items-center gap-2 text-neutral-500 hover:text-white text-sm mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Doble R Records
        </Link>

        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-3">{rs.pageTitle}</h1>
        <p className="text-neutral-400 text-base mb-10 max-w-xl">{rs.pageSubtitle}</p>

        {!result ? (
          <form data-testid="request-status-form" onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col gap-2">
              <Label htmlFor="status-code" className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{rs.inputLabel}</Label>
              <Input
                id="status-code" data-testid="request-status-input" required
                value={code} onChange={(e) => setCode(e.target.value)}
                placeholder={rs.inputPlaceholder} disabled={loading}
                className="bg-white/[0.03] border-white/10 text-white placeholder:text-neutral-500 h-12 text-base"
              />
            </div>

            {error && (
              <div data-testid="request-status-error" className="flex items-start gap-2.5 rounded-xl border border-brand-red/30 bg-brand-red/[0.06] p-3">
                <AlertCircle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
                <p className="text-neutral-300 text-xs leading-relaxed">{error}</p>
              </div>
            )}

            <button
              type="submit"
              data-testid="request-status-submit-button"
              disabled={!code.trim() || loading}
              className="w-full bg-brand-red text-white font-bold text-sm py-3.5 rounded-full flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:bg-brand-redDark shadow-[0_8px_24px_-6px_rgba(255,36,54,0.5)] transition-all duration-300 disabled:opacity-40 disabled:pointer-events-none"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {rs.submitButton}
            </button>
          </form>
        ) : (
          <div data-testid="request-status-result" className="flex flex-col gap-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 flex flex-col gap-4">
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-xs text-neutral-500">{result.code}</span>
                {result.status === "signed" ? (
                  <span data-testid="request-status-badge-signed" className="inline-flex items-center gap-1.5 rounded-full bg-brand-blue/10 border border-brand-blue/30 text-brand-blue text-xs font-bold px-3 py-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {rs.statusSigned}
                  </span>
                ) : (
                  <span data-testid="request-status-badge-review" className="inline-flex items-center gap-1.5 rounded-full bg-brand-red/10 border border-brand-red/30 text-brand-red text-xs font-bold px-3 py-1">
                    <ClipboardCheck className="w-3.5 h-3.5" /> {rs.statusUnderReview}
                  </span>
                )}
              </div>

              <div>
                <p className="text-neutral-500 text-xs uppercase tracking-wide font-semibold mb-1">{rs.resultArtistLabel}</p>
                <p data-testid="request-status-artist" className="text-white text-lg font-bold">{result.artistOrLabel}</p>
              </div>

              <div>
                <p className="text-neutral-500 text-xs uppercase tracking-wide font-semibold mb-1">{rs.resultSubmittedLabel}</p>
                <p data-testid="request-status-submitted-date" className="text-neutral-300 text-sm">{formatDate(result.submitted_at)}</p>
              </div>

              <p data-testid="request-status-info-message" className="text-neutral-400 text-sm leading-relaxed border-t border-white/10 pt-4">
                {result.status === "signed"
                  ? `${rs.signedInfo} ${formatDate(result.signed_at)}.`
                  : rs.underReviewInfo}
              </p>
            </div>

            <button
              type="button"
              data-testid="request-status-search-another-button"
              onClick={handleReset}
              className="text-sm font-semibold text-white px-6 py-3 rounded-full border border-white/15 hover:bg-white/[0.06] transition-all duration-300"
            >
              {rs.searchAnotherButton}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
